import React, { useEffect, useMemo, useRef, useState } from 'react';
import supabase from '../config/supabase';
import {
  getCachedVendorDirectory,
  normalizeVendorName,
  searchVendorSuggestions
} from '../utils/vendorResolution';

const wrapperStyle = { position: 'relative' };
const dropdownStyle = {
  position: 'absolute',
  zIndex: 1000,
  top: '100%',
  left: 0,
  right: 0,
  background: '#fff',
  border: '1px solid #d7cbc0',
  borderRadius: '8px',
  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
  marginTop: '4px',
  maxHeight: '220px',
  overflowY: 'auto'
};
const optionStyle = {
  width: '100%',
  textAlign: 'left',
  border: 'none',
  background: 'transparent',
  padding: '8px 10px',
  cursor: 'pointer'
};

const VendorAutocompleteInput = ({
  value,
  onChange,
  storeId,
  className = 'form-control form-control-sm',
  placeholder = 'Vendor',
  inputProps = {},
  forceCreateLabel = null
}) => {
  const [directory, setDirectory] = useState({ vendors: [], aliases: [], exactVendorByKey: new Map(), aliasByKey: new Map() });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const cleanValue = normalizeVendorName(value);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!storeId) return;
      setLoading(true);
      try {
        const nextDirectory = await getCachedVendorDirectory({
          supabaseClient: supabase,
          storeId,
          forceRefresh: false
        });
        if (mounted) setDirectory(nextDirectory);
      } catch (error) {
        console.warn('Failed to load vendor directory for autocomplete:', error?.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [storeId]);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  const suggestions = useMemo(() => searchVendorSuggestions(directory, cleanValue, 8), [directory, cleanValue]);

  const hasExplicitCreate = cleanValue && !suggestions.some((entry) => normalizeVendorName(entry.label).toLowerCase() === cleanValue.toLowerCase());

  return (
    <div ref={wrapperRef} style={wrapperStyle}>
      <input
        {...inputProps}
        type="text"
        value={value}
        className={className}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
      />
      {open && (suggestions.length > 0 || hasExplicitCreate || loading) && (
        <div style={dropdownStyle}>
          {loading && (
            <div style={{ padding: '8px 10px', color: '#8B7B73', fontSize: '0.75rem' }}>
              Loading vendors...
            </div>
          )}
          {suggestions.map((entry) => (
            <button
              key={entry.id}
              type="button"
              style={optionStyle}
              onClick={() => {
                onChange(entry.label);
                setOpen(false);
              }}
            >
              <div style={{ fontWeight: 600, color: '#5A4A42' }}>{entry.label}</div>
              <div style={{ fontSize: '0.72rem', color: '#8B7B73' }}>{entry.meta}</div>
            </button>
          ))}
          {hasExplicitCreate && (
            <button
              type="button"
              style={{ ...optionStyle, borderTop: '1px solid #f0e7df' }}
              onClick={() => {
                onChange(cleanValue);
                setOpen(false);
              }}
            >
              <div style={{ fontWeight: 600, color: '#226e3a' }}>
                + {forceCreateLabel || `Create new vendor "${cleanValue}"`}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#8B7B73' }}>
                New vendor will be created only if no exact or alias match exists.
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default VendorAutocompleteInput;
